import { isObject } from "lodash";
import mime from "mime";
import imageCompression, { Options } from 'browser-image-compression';
import { getImageBlob } from "../redux/request/apis";

export function getBase64Image(img, callback) {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result));
  reader.readAsDataURL(img);
}

export function dataURLtoBlob(dataurl) {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

export function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

export function addImageProcess(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ height: img.height, width: img.width });
    img.onerror = reject;
    img.src = src;
  });
}

export async function getFileFromUrl(url) {
  const data = await getImageBlob({
    params: { url, withAuthHeader: await isWebGWFile(url) },
  });
  const name = url.split("/").pop();
  const type = mime.getType(url);
  return new File([data], name, { type });
}

export const getTokenImage = async (img) => {
  try {
    const isFile = isObject(img) && "url" in img;
    const url = isFile ? img.url : img;
    const data = await getImageBlob({ params: { url, withAuthHeader: true } });
    const tokenUrl = URL.createObjectURL(data);
    return isFile ? { ...img, url: tokenUrl } : tokenUrl;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const isWebGWFile = (file) => {
  try {
    let webGwUrl = process.env.REACT_APP_API_URL;
    if (!webGwUrl) throw new Error("REACT_APP_API_URL is not defined");
    if (webGwUrl.startsWith("//"))
      webGwUrl = webGwUrl.replace("//", "https://");
    const isFile = isObject(file) && "url" in file;
    let url = isFile ? file.url : file;
    const parsedUrl = new URL(url);
    return parsedUrl.hostname === new URL(webGwUrl).hostname;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const compressImage = async (file, options = {}) => {
  // 验证输入文件
  if (!(file instanceof File) && !(file instanceof Blob)) {
    throw new Error('file given is not an instance of Blob or File');
  }
  
  // eslint-disable-next-line no-console
  console.log(`before compress file size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
  
  const fileSizeMB = file.size / 1024 / 1024;
  
  // 如果文件很小，直接返回
  if (fileSizeMB <= 2) {
    // eslint-disable-next-line no-console
    console.log('File is small enough, skipping compression');
    return file;
  }
  
  // 根据文件大小动态设置初始质量 (0.5-0.9)
  // 图片越大，质量越小；图片越小，质量越大
  const calculateInitialQuality = (sizeMB) => {
    // 2MB：质量0.9 (较高质量)
    // 2-4MB：线性递减到0.85
    // 4-6MB：线性递减到0.8
    // 6-8MB：线性递减到0.75
    // 8-10MB：线性递减到0.7
    // 10-50MB：线性递减到0.5
    // 50MB以上：质量0.5 (较低质量)
    if (sizeMB <= 2) {
      return 0.9;
    }
    if (sizeMB <= 4) {
      // 2MB到4MB，质量从0.9递减到0.85
      return 0.9 - (sizeMB - 2) * (0.9 - 0.85) / (4 - 2);
    }
    if (sizeMB <= 6) {
      // 4MB到6MB，质量从0.85递减到0.8
      return 0.85 - (sizeMB - 4) * (0.85 - 0.8) / (6 - 4);
    }
    if (sizeMB <= 8) {
      // 6MB到8MB，质量从0.8递减到0.75
      return 0.8 - (sizeMB - 6) * (0.8 - 0.75) / (8 - 6);
    }
    if (sizeMB <= 10) {
      // 8MB到10MB，质量从0.75递减到0.7
      return 0.75 - (sizeMB - 8) * (0.75 - 0.7) / (10 - 8);
    }
    if (sizeMB <= 50) {
      // 10MB到50MB，质量从0.7递减到0.5
      return 0.7 - (sizeMB - 10) * (0.7 - 0.5) / (50 - 10);
    }
    return 0.5;
  };
  
  const dynamicInitialQuality = calculateInitialQuality(fileSizeMB);
  
  // eslint-disable-next-line no-console
  console.log(`Dynamic initial quality: ${dynamicInitialQuality.toFixed(2)} for ${fileSizeMB.toFixed(2)}MB file`);
  
  const defaultOptions = {
    maxSizeMB: 10, // 默认目标大小
    maxWidthOrHeight: 10000, // 限制最大边长
    useWebWorker: false, // 关闭WebWorker提高速度
    fileType: 'image/jpeg', // 强制JPEG格式，压缩更快
    initialQuality: dynamicInitialQuality, // 根据文件大小动态设置初始质量
  };

  const compressionOptions = { ...defaultOptions, ...options };
  const targetSizeMB = compressionOptions.maxSizeMB;
  let currentQuality = compressionOptions.initialQuality;

  try {
    const startTime = performance.now();
    let compressed = await imageCompression(file, compressionOptions);
    let compressionTime = performance.now() - startTime;
    
    // eslint-disable-next-line no-console
    console.log(`First compression took ${compressionTime.toFixed(2)}ms`);
    
    let attempts = 1;
    const maxAttempts = 5; // 最多尝试5次
    
    // 如果压缩后仍然超过目标大小，降低质量重试
    while (compressed.size / 1024 / 1024 > targetSizeMB && attempts < maxAttempts) {
      const currentSizeMB = compressed.size / 1024 / 1024;
      attempts += 1;
      
      // 每次降低质量0.1，最低不低于0.2
      currentQuality = Math.max(0.2, currentQuality - 0.1);
      
      const retryOptions = {
        ...compressionOptions,
        initialQuality: currentQuality,
      };
      
      // eslint-disable-next-line no-console
      console.log(`Retry ${attempts}: quality ${currentQuality}, size ${retryOptions.maxWidthOrHeight}`);
      
      const retryStartTime = performance.now();
      // eslint-disable-next-line no-await-in-loop
      const recompressed = await imageCompression(file, retryOptions);
      const retryTime = performance.now() - retryStartTime;
      
      compressionTime += retryTime;
      
      // 如果新压缩结果更好，使用新结果
      if (recompressed.size < compressed.size) {
        compressed = recompressed;
        // eslint-disable-next-line no-console
        console.log(`Retry ${attempts} successful: ${(recompressed.size / 1024 / 1024).toFixed(2)}MB in ${retryTime.toFixed(2)}ms`);
      } else {
        break;
      }
    }
    
    const finalSizeMB = compressed.size / 1024 / 1024;
    
    // 如果压缩后文件没有变小，返回原文件
    if (compressed.size >= file.size) {
      // eslint-disable-next-line no-console
      console.warn('Compression did not reduce file size, returning original file');
      return file;
    }
    
    // 快速像素检查
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        const totalPixels = img.width * img.height;
        if (totalPixels > 75000000) {
          reject(new Error('Compressed image exceeds maximum pixel count of 75 million'));
        } else {
          // eslint-disable-next-line no-console
          console.log(`Final result: ${finalSizeMB.toFixed(2)}MB, ${attempts} attempts, ${compressionTime.toFixed(2)}ms total`);
          resolve(compressed);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load compressed image'));
      };
      
      img.src = URL.createObjectURL(compressed);
    });
  } catch (error) {
    throw new Error(`Image compression failed: ${error.message}`);
  }
};


export const upscaleImage = (file, targetWidth, targetHeight) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
      canvas.toBlob((blob) => {
        if (blob) {
          const upscaledFile = new File([blob], file.name, { type: 'image/jpeg' });
          console.log(`Image upscaled to ${targetWidth}x${targetHeight}`);
          resolve(upscaledFile);
        } else {
          reject(new Error('Upscaling failed'));
        }
      }, 'image/jpeg', 0.9);
    };
    img.onerror = err => reject(err);
    img.src = e.target.result;
  };
  reader.onerror = err => reject(err);
  reader.readAsDataURL(file);
});
